// ====================================================================
// ziven-core forum DiscussionThumbnails
// ====================================================================
//
// 帖子列表页缩略图小黑盒样式 (辉哥 9/4 15:13 反馈, 派 Coder 干).
// 跟 modifyDiscussionItem.js jQuery 拼字符串模式不同,
// 走 mithril 组件, 但保留 1 个静态 renderHtml() 给 jQuery fallback 用 (mobile
// 模式整卡片重组是 jQuery 注入另一张卡片, 跟 mithril 渲染管线分离).
//
// Props: discussion (Flarum Discussion model)
// 读 discussion.imageCount() + discussion.imageList() (新加的
// ApiResource attributes, 见 src/Api/DiscussionAttributes.php)
//
// 渲染规则 (辉哥拍板):
//   - 0 张: return null (不显示缩略图区)
//   - 1 张: 1 张大图 (aspect-ratio: 16/9, 全宽)
//   - 2-3 张: 横排并排 (aspect-ratio: 4/3, gap: 4-8px)
//   - 4+ 张: 前 3 张 + 右上角 N+张 徽章 (绝对定位, N = imageCount - 3)
//
// 样式: less/forum.less 加 .DiscussionThumbnails (深色卡片背景 + 圆角 8px +
//       object-fit: cover + max-width: 100%)
//
// 改 (辉哥 9/9 00:55 反馈 "点击帖子图片区域也要跳转到帖子详情页中"):
//   desktop 模式: mithril 渲染时用 <Link href={app.route.discussion(discussion)}> 包裹 .DiscussionThumbnails
//     整个缩略图区域变成可点击的 <a>, mithril Link 自动处理 SPA 导航 (用 m.route.set 不会全页刷新)
//   mobile 模式: jQuery 渲染时给 .DiscussionListImageThumbnailWrap 绑 click handler, m.route.set 跳详情
//     移动模式不渲染 <a> 包裹 (走 jQuery 整卡片重组, 跟 mithril 渲染管线分离)
//   实战教训: DiscussionListItem-main 已经是 <a> 链接, DiscussionThumbnails 是注入到它内部, 不能嵌套 <a>
//     改用 <Link> 包裹 .DiscussionThumbnails 子 div, 跟 vendor DiscussionListItem-main 同级 / 嵌套深度 1
//
// 改 (辉哥 9/9 01:00 反馈 "改完后移动端帖子列表页评论数那块的样式乱掉了"):
//   用 <Link> 包裹 .DiscussionThumbnails 嵌套在 vendor <a class="DiscussionListItem-main"> 内部
//   HTML 规范不允许 <a> 嵌套 <a>, 浏览器解析时把嵌套 <a> 提取出来重排, 导致 .item-discussion-views
//   后面突然多一个 <a> 撑高容器, 评论数样式错位
//   修法: 不用 <Link> 包裹, 改用 click handler (e.stopPropagation + m.route.set) 实现点击跳转
//   缩略图区域仍是 cursor: pointer, 视觉上像链接, 但 DOM 树没有 <a> 嵌套
//   vendor <a class="DiscussionListItem-main"> 已经覆盖整卡片, 用户点缩略图实际是点 vendor 链接跳转
//     我们的 click handler 拦截缩略图 click, e.preventDefault 阻止 vendor 链接触发, 自己走 SPA 路由
//   mobile 模式: 同样用 click handler, 不用 <a> 包裹
// ====================================================================
import app from 'flarum/forum/app';

// t() 扁平化 — ziven-dress-up 沉淀的 i18n 习惯 + Symfony ICU MessageFormat 支持
// Symfony translator 返数组 (字面值 + placeholder 拆开), 必须 join('') 才能渲染
function t(key, params) {
  const result = params ? app.translator.trans(key, params) : app.translator.trans(key);
  return Array.isArray(result) ? result.join('') : result;
}
export default class DiscussionThumbnails {
  /**
   * 静态方法: 返 HTML 字符串 (供 jQuery append 用, mobile 模式用)
   * @param {Array} imageList - URL 数组 (Flarum attribute, 已经是 array, 限 max 后)
   * @param {number} imageCount - 总图数 (不限 max, 用于 4+ 徽章 "N+张")
   * @param {Object} [discussion] - 可选, Flarum Discussion model, 用于偶数 ID 判 alt 布局
   * @returns {string} HTML 字符串
   */
  static renderHtml(imageList, imageCount, discussion) {
    // 读 max setting, max=0 (禁用) 不渲染缩略图
    // 改 max=0 不会清空已存在帖子的 imageList, 所以前端必须自己判断
    const maxCount = (() => {
      try {
        const v = app.forum.attribute('zivenCoreThumbnailsMaxCount');
        return typeof v === 'number' ? v : 3;
      } catch (e) {
        return 3;
      }
    })();
    if (maxCount <= 0) return '';
    if (!imageCount || !imageList || imageList.length === 0) return '';
    if (!Array.isArray(imageList)) return '';

    // (辉哥 9/21 21:10 拍板): 偶数 ID 帖子 DiscussionThumbnails 改 1 大 + 2 小布局
    //   触发: discussion.id() % 2 === 0 时加 --alt class (CSS grid 1 大 + 2 小)
    //   仅影响 2+ 张情况 (1 张不变 --single 全宽 16/9)
    //   discussion 可选 — 没传或 id() 抛错时 fallback 走原逻辑 (奇数 ID 同 3 横排)
    let isAltLayout = false;
    if (discussion && typeof discussion.id === 'function') {
      try {
        const id = discussion.id();
        // id 可能是 number 也可能是 string, Number() 兜底
        isAltLayout = Number(id) % 2 === 0;
      } catch (e) {
        isAltLayout = false;
      }
    }

    // 1 张: 大图 (全宽 16/9) — 单图不受偶数 ID 影响
    if (imageCount === 1) {
      return "<div class=\"DiscussionThumbnails DiscussionThumbnails--single\">\n        <div class=\"DiscussionThumbnails-image\" style=\"background-image: url('".concat(imageList[0], "')\"></div>\n      </div>");
    }

    // 2-3 张: 横排并排 (imageCount 限 3 张以内就并排)
    //   偶数 ID 改 --alt 1 大 + 2 小布局 (左大图占左整列 + 右上小图 + 右下小图)
    if (imageCount <= 3) {
      const items = imageList.map(url => "<div class=\"DiscussionThumbnails-image\" style=\"background-image: url('".concat(url, "')\"></div>")).join('');
      const altClass = isAltLayout ? ' DiscussionThumbnails--alt' : '';
      return "<div class=\"DiscussionThumbnails DiscussionThumbnails--multi".concat(altClass, "\">").concat(items, "</div>");
    }

    // 4+ 张: 显示 imageList 全部 (受 max 限) + 徽章
    //   (辉哥 2026-09-04 18:57 反馈): 徽章显示**图片总数** (例 imageCount=5 → "共5张"),
    //     不是隐藏的 overflow 数 (之前 max=3, imageCount=5 → "2+张" 改成 max=3, imageCount=5 → "共5张")
    //     让用户一眼看出发帖含图总数, 跟"列表项看到 N 张 → 帖子含 N 张"逻辑一致
    //   偶数 ID 改 --alt 1 大 + 2 小 + 徽章 (徽章 absolute 右下, 新布局下视觉叠加在右下小图)
    const items = imageList.map(url => "<div class=\"DiscussionThumbnails-image\" style=\"background-image: url('".concat(url, "')\"></div>")).join('');
    // Symfony translator 传 params 让 {n} 正确替换, t() 处理数组 join
    const badgeText = t('ziven-community-core.forum.thumbnail_overflow', {
      n: imageCount
    });
    const altClass = isAltLayout ? ' DiscussionThumbnails--alt' : '';
    return "<div class=\"DiscussionThumbnails DiscussionThumbnails--multi DiscussionThumbnails--overflow".concat(altClass, "\">\n      ").concat(items, "\n      <div class=\"DiscussionThumbnails-badge\">").concat(badgeText, "</div>\n    </div>");
  }

  /**
   * mithril view() — 给 extend(DiscussionListItem.prototype, 'mainView') 用
   * 渲染 raw HTML (m.trust 信任), 跟静态方法共用同一份 HTML 生成逻辑
   *
   * 改 (辉哥 9/9 00:55 反馈 "点击帖子图片区域也要跳转到帖子详情页中"):
   *   用 click handler 让缩略图区域可点击, 走 SPA 导航 (m.route.set, 不全页刷新)
   *
   * 改 (辉哥 9/9 01:00 反馈 "改完后移动端帖子列表页评论数那块的样式乱掉了"):
   *   用 <Link> 包裹, 但 vendor <a class="DiscussionListItem-main"> 已经覆盖整卡片
   *   <a> 嵌套 <a> 浏览器解析会重排 DOM, 导致 .item-discussion-views 后面多 <a> 撑高容器
   *   修法: 改用 onclick handler (e.stopPropagation + m.route.set), 不再 <Link> 包裹
   *   vendor 父 <a> 仍然是链接区域, 我们的 click handler 拦截缩略图 click, 阻止冒泡
   */
  view(vnode) {
    const discussion = vnode.attrs.discussion;
    if (!discussion) return null;

    // 读 max setting, max=0 (禁用) 不渲染
    const maxCount = (() => {
      try {
        const v = app.forum.attribute('zivenCoreThumbnailsMaxCount');
        return typeof v === 'number' ? v : 3;
      } catch (e) {
        return 3;
      }
    })();
    if (maxCount <= 0) return null;

    // Flarum 2.0 序列化器 camelCase 自动 from snake_case
    const imageCount = discussion.imageCount ? discussion.imageCount() : discussion.attribute('imageCount') || 0;
    const imageList = discussion.imageList ? discussion.imageList() : discussion.attribute('imageList') || [];
    if (!imageCount || !imageList || imageList.length === 0) return null;
    const html = DiscussionThumbnails.renderHtml(imageList, imageCount, discussion);
    if (!html) return null;

    // 改用 onclick handler, 不再 <Link> 包裹 (避免嵌套 <a> 浏览器重排)
    //   vendor 父 <a class="DiscussionListItem-main"> 仍是整卡片链接
    //   我们的 click handler 拦截缩略图 click, e.stopPropagation 阻止 vendor 链接触发, 走 m.route.set SPA 跳转
    return m('.DiscussionThumbnailsWrapper', {
      onclick: e => {
        e.stopPropagation();
        e.preventDefault();
        m.route.set(app.route.discussion(discussion));
      }
    }, m.trust(html));
  }
}
flarum.reg.add('ziiven-ziven-core', 'forum/components/DiscussionThumbnails', DiscussionThumbnails);